import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentaryCard from './DocumentaryCard';
import styles from './css/DocumentFullPage.module.css';
import { useVideosByCategory } from '../../hooks/useVideosHooks';
import { useParams } from 'react-router-dom';
import Loading from '../../common/HousGlass Loader/Loading';
import Footer from '../../common/UI/Footer';

const DocumentarySection = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Get videos for the category
  const {
    videos,
    isLoading,
    error,
    totalPages
  } = useVideosByCategory(category, currentPage, itemsPerPage);

  // Handle navigation to video player
  const handleWatchNow = (documentary) => {
    // Navigate to video player page with documentary slug
    navigate(`/watch/${documentary.slug}`, { 
      state: { documentary } 
    });
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return <div className={styles.loading}><Loading /></div>;
  }

  if (error) {
    return <div className={styles.error}>Error loading documentaries.</div>;
  }

  if (!videos || videos.length === 0) {
    return (
      <div className={styles.noContent}>
        <h2>No documentaries found</h2>
        <p>Check back later for new content.</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.documentarySection}>
        {/* Main Content */}
        <div className={styles.documentariesContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.pageTitle}>
              {category 
                ? category.charAt(0).toUpperCase() + category.slice(1) 
                : 'Documentaries'
              }
            </h2>
            
          </div>

          <div className={styles.cardContainer}>
            {videos.map((doc) => (
              <DocumentaryCard 
                key={doc.id} 
                documentary={doc}
                onPlay={() => handleWatchNow(doc)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button 
                className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              <div className={styles.pageNumbers}>
                {/* Show first page */}
                {currentPage > 3 && (
                  <>
                    <button
                      className={styles.pageNumber}
                      onClick={() => handlePageChange(1)}
                    >
                      1
                    </button>
                    {currentPage > 4 && <span className={styles.ellipsis}>...</span>}
                  </>
                )}
                
                {/* Show current page and surrounding pages */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page >= Math.max(1, currentPage - 2) && 
                    page <= Math.min(totalPages, currentPage + 2)
                  )
                  .map((page) => (
                    <button
                      key={page}
                      className={`${styles.pageNumber} ${page === currentPage ? styles.activePage : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                
                {/* Show last page */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && <span className={styles.ellipsis}>...</span>}
                    <button
                      className={styles.pageNumber}
                      onClick={() => handlePageChange(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              
              <button 
                className={`${styles.paginationButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer/>
    </>
  );
};

export default DocumentarySection;